import { v4 } from "uuid";
import { isEmpty, remove } from "lodash";
import DOMPurify from "dompurify";
import GET_SHIPPING_DATA from "../queries/get-shipping-data";
import { client } from "../apollo/client";
import { navigate } from "gatsby";
import { authStore } from "./tokenStore";

export const normalizePath = (path) => {
  const pathStr = path.split("/");

  // If the path ends with '/' get the second last item
  if (path?.endsWith(`/`)) {
    const strIndex = pathStr.length ? pathStr.length - 2 : "";

    if (strIndex) {
      path = `/${pathStr[strIndex]}/`;
    }
  }

  // If the path ends with '/' get the second last item.
  if (!path?.endsWith(`/`)) {
    const strIndex = pathStr.length ? pathStr.length - 1 : "";

    if (strIndex) {
      path = `/${pathStr[strIndex]}/`;
    }
  }

  return path;
};
/**
 * Get date in format of m-d-y
 *
 * @param {string} dateString Date string, example 2020-05-03T04:41:12
 *
 * @return {string}
 */
export const getFormattedDate = (dateString) => {
  if (!dateString) {
    return "";
  }

  const date = new Date(dateString);

  return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
};

/**
 * Extracts and returns float value from a string.
 *
 * @param {string} string String
 * @return {any}
 */
export const getFloatVal = (string) => {
  let floatValue = string.match(/[+-]?\d+(\.\d+)?/g)[0];
  return null !== floatValue
    ? parseFloat(parseFloat(floatValue).toFixed(2))
    : "";
};

/**
 * Add first product.
 *
 * @param {Object} product Product
 * @return {{totalProductsCount: number, totalProductsPrice: any, products: Array}}
 */
export const addFirstProduct = (product) => {
  let productPrice = getFloatVal(product.price);

  let newCart = {
    products: [],
    totalProductsCount: 1,
    totalProductsPrice: productPrice,
  };

  const newProduct = createNewProduct(product, productPrice, 1);
  newCart.products.push(newProduct);

  localStorage.setItem("woo-gatsby-cart", JSON.stringify(newCart));

  return newCart;
};

/**
 * Create a new product object.
 *
 * @param {Object} product Product
 * @param {Integer} productPrice Product Price
 * @param {Integer} qty Quantity
 * @return {{image: *, productId: *, totalPrice: number, price: *, qty: *, name: *}}
 */
export const createNewProduct = (product, productPrice, qty) => {
  //console.log(product)
  return {
    productId: product.productId,
    image: product.image,
    name: product.name,
    price: productPrice,
    link: product.slug,
    // language: product.nodes.language,
    // category: product.nodes.productCategories
    qty,
    totalPrice: parseFloat((productPrice * qty).toFixed(2)),
  };
};

/**
 * Updates the existing cart with new item.
 *
 * @param {Object} existingCart Existing Cart.
 * @param {Object} product Product.
 * @param {Integer} qtyToBeAdded Quantity.
 * @param {Integer} newQty New Qty to be updated.
 * @return {{totalProductsCount: *, totalProductsPrice: *, products: *}}
 */
export const updateCart = (
  existingCart,
  product,
  qtyToBeAdded,
  newQty = false
) => {
  const updatedProducts = getUpdatedProducts(
    existingCart.products,
    product,
    qtyToBeAdded,
    newQty
  );

  const addPrice = (total, item) => {
    total.totalPrice += item.totalPrice;
    total.qty += item.qty;

    return total;
  };

  // Loop through the updated product array and add the totalPrice of each item to get the totalPrice
  let total = updatedProducts.reduce(addPrice, { totalPrice: 0, qty: 0 });

  const updatedCart = {
    products: updatedProducts,
    totalProductsCount: parseInt(total.qty),
    totalProductsPrice: parseFloat(total.totalPrice),
  };
  // console.log("setting cart", updateCart);
  localStorage.setItem("woo-gatsby-cart", JSON.stringify(updatedCart));

  return updatedCart;
};

/**
 * Get updated products array
 * Update the product if it exists else,
 * add the new product to existing cart,
 *
 * @param {Object} existingProductsInCart Existing product in cart
 * @param {Object} product Product
 * @param {Integer} qtyToBeAdded Quantity
 * @param {Integer} newQty New qty of the product (optional)
 * @return {*[]}
 */
export const getUpdatedProducts = (
  existingProductsInCart,
  product,
  qtyToBeAdded,
  newQty = false
) => {
  // Check if the product already exits in the cart.
  const productExitsIndex = isProductInCart(
    existingProductsInCart,
    product.productId
  );

  // If product exits ( index of that product found in the array ), update the product quantity and totalPrice
  if (-1 < productExitsIndex) {
    let updatedProducts = existingProductsInCart;
    let updatedProduct = updatedProducts[productExitsIndex];

    // If have new qty of the product available, set that else add the qtyToBeAdded
    updatedProduct.qty = newQty
      ? parseInt(newQty)
      : parseInt(updatedProduct.qty + qtyToBeAdded);
    updatedProduct.totalPrice = parseFloat(
      (updatedProduct.price * updatedProduct.qty).toFixed(2)
    );

    return updatedProducts;
  } else {
    // If product not found push the new product to the existing product array.
    let productPrice = getFloatVal(product.price);
    const newProduct = createNewProduct(product, productPrice, qtyToBeAdded);
    existingProductsInCart.push(newProduct);

    return existingProductsInCart;
  }
};

/**
 * Returns index of the product if it exists.
 *
 * @param {Object} existingProductsInCart Existing Products.
 * @param {Integer} productId Product id.
 * @return {number | *} Index Returns -1 if product does not exist in the array, index number otherwise
 */
const isProductInCart = (existingProductsInCart, productId) => {
  const returnItemThatExits = (item, index) => {
    if (productId === item.productId) {
      return item;
    }
  };

  // This new array will only contain the product which is matched.
  const newArray = existingProductsInCart.filter(returnItemThatExits);

  return existingProductsInCart.indexOf(newArray[0]);
};

/**
 * Remove Item from the cart.
 *
 * @param {Integer} productId Product Id.
 * @return {any | string} Updated cart
 */
export const removeItemFromCart = (productId) => {
  if (!typeof window !== "undefined") {
    return null;
  }

  let existingCart = localStorage.getItem("woo-gatsby-cart");
  existingCart = JSON.parse(existingCart);

  // If there is only one item in the cart, delete the cart.
  if (1 === existingCart.products.length) {
    localStorage.removeItem("woo-gatsby-cart");
    return null;
  }

  // Check if the product already exits in the cart.
  const productExitsIndex = isProductInCart(existingCart.products, productId);

  // If product to be removed exits
  if (-1 < productExitsIndex) {
    const productTobeRemoved = existingCart.products[productExitsIndex];
    const qtyToBeRemovedFromTotal = productTobeRemoved.qty;
    const priceToBeDeductedFromTotal = productTobeRemoved.totalPrice;

    // Remove that product from the array and update the total price and total quantity of the cart
    let updatedCart = existingCart;
    updatedCart.products.splice(productExitsIndex, 1);
    updatedCart.totalProductsCount =
      updatedCart.totalProductsCount - qtyToBeRemovedFromTotal;
    updatedCart.totalProductsPrice =
      updatedCart.totalProductsPrice - priceToBeDeductedFromTotal;

    //console.log(JSON.stringify(updatedCart));
    localStorage.setItem("woo-gatsby-cart", JSON.stringify(updatedCart));
    return updatedCart;
  } else {
    return existingCart;
  }
};

/**
 * Returns cart data in the required format.
 * @param {Object} data Cart data from API
 */
/**
 * Utility to convert price string "123.45{process.env.GATSBY_CURRENCY_SYMBOL}" to number
 */
const parsePrice = (priceStr) =>
  Number(priceStr.replace(/[^\d.,]/g, "").replace(",", "."));

/**
 * Returns cart data in the required format.
 * @param {Object} data Cart data
 */
export const getFormattedCart = (data) => {
  if (!data?.cart?.contents?.nodes?.length) return null;
  console.log(data);
  const givenProducts = data?.cart?.contents?.nodes;
  const appliedCoupons = data?.cart?.appliedCoupons;

  const cart = {
    appliedCoupons: appliedCoupons,
    products: [],
    shippingTotal: 0,
    subtotal: 0,
    discountTax: data.cart.discountTax,
    discountTotal: data.cart.discountTotal,
    shippingTax: data.cart.shippingTax,
    subtotalTax: data.cart.subtotalTax,
    total: data.cart.total,
    totalTax: data.cart.totalTax,
    totalProductsCount: 0,
    totalProductsPrice: 0,
  };

  // Process products
  givenProducts.forEach(
    ({ product, key, quantity, total, subtotalTax, variation }) => {
      const productData = product?.node;
      if (!productData) return;

      const price = total.replace(/,/g, ".");
      const totalPrice = (
        Number(price.replace(process.env.GATSBY_CURRENCY_SYMBOL, "")) +
        Number(subtotalTax.replace(/,/g, "."))
      ).toFixed(2);

      const category = product?.nodes?.productCategories?.nodes[0]?.name || "";
      const link = productData.link.replace("https://superfunky.space", "");
      const image = productData?.image || {
        sourceUrl: "https://via.placeholder.com/434",
        srcSet: "https://via.placeholder.com/434",
        title: productData.name,
      };

      const productInfo = {
        productId: productData.databaseId,
        cartKey: key,
        name: productData.name,
        qty: quantity,
        price: price,
        category: category,
        totalPrice: `${totalPrice}${process.env.GATSBY_CURRENCY_SYMBOL}`,
        link,
        image,
      };

      // console.log(productInfo);

      if (productData.type === "VARIABLE") productInfo.variation = variation;

      cart.subtotal += Number(
        price.replace(process.env.GATSBY_CURRENCY_SYMBOL, "")
      );
      cart.products.push(productInfo);
      cart.totalProductsCount += quantity;
    }
  );
  // console.log(cart);
  // Initial totals before shipping
  cart.totalProductsPrice = cart.subtotal;

  return cart;
};

/**
 * Async function to patch shipping & totals after cart creation
 */
export const patchShipping = async (cart, shipCountry) => {
  if (!cart || !shipCountry) return cart;

  try {
    const { data } = await client.query({
      query: GET_SHIPPING_DATA,
      variables: { countries: [shipCountry] },
      fetchPolicy: "network-only",
    });

    const countryData = data?.shippingMethodsData?.find(
      (c) => c.country === shipCountry
    );
    if (!countryData) return cart;

    let shippingAmount = 0;
    const freeShipping = countryData.methods.find((m) =>
      m.id.startsWith("free_shipping")
    );

    // 🔑 detect coupon with "free shipping" in description
    const hasFreeShippingCoupon =
      cart.appliedCoupons?.some((c) =>
        c.description?.toLowerCase().includes("free shipping")
      ) || false;

    if (hasFreeShippingCoupon && freeShipping) {
      // ✅ Free shipping only if coupon + method exists
      shippingAmount = 0;
    } else if (
      freeShipping &&
      cart.subtotal >= freeShipping.freeShippingMinAmount
    ) {
      // ✅ Free shipping by threshold
      shippingAmount = 0;
    } else {
      const defaultMethod = countryData.methods.find(
        (m) => !m.id.startsWith("free_shipping")
      );
      shippingAmount = defaultMethod ? defaultMethod.total : 0;
    }

    // Patch cart shipping & totals
    cart.shippingTotal = `${shippingAmount.toFixed(2)}${
      process.env.GATSBY_CURRENCY_SYMBOL
    }`;

    let totalProductsPrice = cart.subtotal + shippingAmount;

    cart.totalProductsPrice = `${totalProductsPrice.toFixed(2)}${
      process.env.GATSBY_CURRENCY_SYMBOL
    }`;
  } catch (err) {
    console.error("Error fetching shipping data:", err);
  }

  return cart;
};

export const createCheckoutData = (order) => {
  const checkoutData = {
    clientMutationId: v4(),
    billing: {
      firstName: order.firstName,
      lastName: order.lastName,
      address1: order.address1,
      address2: order.address2,
      city: order.city,
      country: order.country,
      state: order.state,
      postcode: order.postcode,
      email: order.email,
      phone: order.phone,
      company: order.company,
    },
    currency: order.currency,
    paymentMethod: "stripe",
    isPaid: false,
    transactionId: v4(),
    customerNote: order.customerNote,
  };
  //console.log(order.shipToDifferentAddress)
  if (order.shipToDifferentAddress) {
    checkoutData.shipToDifferentAddress = true;
    checkoutData.shipping = {
      firstName: order.firstName2,
      lastName: order.lastName2,
      address1: order.address12,
      address2: order.address22,
      city: order.city2,
      country: order.country2,
      state: order.state2,
      postcode: order.postcode2,
      phone: order.phone2,
      company: order.company2,
    };
  } else {
    checkoutData.shipping = {
      firstName: order.firstName,
      lastName: order.lastName,
      address1: order.address1,
      address2: order.address2,
      city: order.city,
      country: order.country,
      state: order.state,
      postcode: order.postcode,
      phone: order.phone,
      company: order.company,
    };
  }

  if (order.createAccount) {
    checkoutData.account = {
      username: order.username,
      password: order.password,
    };
  }
  //console.log(checkoutData)
  return checkoutData;
};

/**
 * Get the updated items in the below format required for mutation input.
 *
 * [
 * { "key": "33e75ff09dd601bbe6dd51039152189", "quantity": 1 },
 * { "key": "02e74f10e0327ad868d38f2b4fdd6f0", "quantity": 1 },
 * ]
 *
 * Creates an array in above format with the newQty (updated Qty ).
 *
 */
export const getUpdatedItems = (products, newQty, cartKey) => {
  // Create an empty array.
  const updatedItems = [];

  // Loop through the product array.
  products.map((cartItem) => {
    // If you find the cart key of the product user is trying to update, push the key and new qty.
    if (cartItem.cartKey === cartKey) {
      updatedItems.push({
        key: cartItem.cartKey,
        quantity: parseInt(newQty),
      });

      // Otherwise just push the existing qty without updating.
    } else {
      updatedItems.push({
        key: cartItem.cartKey,
        quantity: cartItem.qty,
      });
    }
    return null;
  });

  // Return the updatedItems array with new Qtys.
  return updatedItems;
};

export const isUserLoggedIn = () => {
  let authData;

  if (typeof window !== "undefined") {
    authData = JSON.parse(localStorage.getItem("auth"));
  }
  return authData;
};

export const logOut = () => {
  // Clear in-memory token immediately so no further requests use it
  authStore.clear();
  // Hard redirect — forces a full page reload which kills all intervals,
  // Apollo links, and React state immediately (unlike Gatsby's navigate)
  if (typeof window !== "undefined") {
    window.location.replace("/auth");
  } else {
    navigate("/auth");
  }
};

export const setAuth = (authData) => {
  localStorage.setItem("auth", JSON.stringify(authData));
};

/**
 * Check if user is logged in.
 *
 * @return {object} Auth Object containing token and user data, false on failure.
 */
export const isUserValidated = () => {
  let userLoggedInData = "";

  if (typeof window !== "undefined") {
    let authTokenData = localStorage.getItem("auth");

    if (!isEmpty(authTokenData)) {
      authTokenData = JSON.parse(authTokenData);

      if (!isEmpty(authTokenData.authToken)) {
        userLoggedInData = authTokenData;
      }
    }
  }

  return userLoggedInData;
};

/**
 * Function to get opengraph image.
 *
 * @param {Object} seo Seo data.
 *
 * @return {void}
 */
export const getOgImage = (seo) => {
  if (
    isEmpty(seo) ||
    isEmpty(seo.opengraphImage) ||
    isEmpty(seo.opengraphImage.sourceUrl)
  ) {
    return getDefaultOgImage(seo);
  }

  return seo.opengraphImage.sourceUrl;
};

/**
 * Function to get opengraph default image.
 *
 * @param {Object} seo Seo data.
 *
 * @return {void}
 */
export const getDefaultOgImage = (seo) => {
  if (
    isEmpty(seo) ||
    isEmpty(seo.social) ||
    isEmpty(seo.social.facebook) ||
    isEmpty(seo.social.facebook.defaultImage) ||
    isEmpty(seo.social.facebook.defaultImage.sourceUrl)
  ) {
    return "";
  }

  return seo.social.facebook.defaultImage.sourceUrl;
};

/**
 * Add to wish list
 * @param {Object} productData Product data.
 */
export const addToWishList = (productData) => {
  let updatedWishList;

  // Get the existing value of wishlist from localStorage.
  const existingWishList = JSON.parse(localStorage.getItem("woo_wishlist"));

  /**
   * If its the first item
   */

  // Set it in localStorage and return.
  if (isEmpty(existingWishList)) {
    updatedWishList = addWishListToLocalStorage({
      productIds: [productData.node.databaseId],
      products: [productData],
    });
    return updatedWishList;
  }

  /**
   * If its not the first item
   */

  // First set the updated wishlist to existing one.
  updatedWishList = existingWishList;

  // Then push the new items to existing array.
  if (!existingWishList.productIds.includes(productData.node.databaseId)) {
    updatedWishList.productIds.push(productData.node.databaseId);
    updatedWishList.products.push(productData);
  }

  // Update the localStorage with updated items.
  addWishListToLocalStorage(updatedWishList);
};

/**
 * Remove item from the list.
 *
 * @param productId
 * @param getWishList
 * @param setWishList
 */
export const removeProductFromWishList = (
  productId,
  getWishList,
  setWishList
) => {
  const existingWishlist = getWishListProducts();
  let updatedWishList;
  // console.log(productId)
  if (!isEmpty(existingWishlist)) {
    const updatedItems = {
      productIds: remove(existingWishlist.productIds, (id) => {
        return productId !== id;
      }),
      products: remove(existingWishlist.products, (product) => {
        return productId !== product.node.databaseId;
      }),
    };

    addWishListToLocalStorage(updatedItems);

    if (0 === updatedItems.productIds.length) {
      setWishList(updatedItems);
    } else {
      updatedWishList = setWishList(updatedItems);
      getWishList();
      //window.location.reload(true)
    }
    return updatedWishList;
  }
};

/**
 * Add wishlist products to localStorage.
 *
 * @param wishList
 */
export const addWishListToLocalStorage = (wishList) => {
  return localStorage.setItem("woo_wishlist", JSON.stringify(wishList));
};

/**
 * Checks if the product with given id exists in the wishlist.
 *
 * @param productId
 * @returns {boolean}
 */
export const isProductInWishList = (productId) => {
  if (!typeof window !== "undefined") {
    return null;
  }
  const existingWishList = JSON.parse(localStorage.getItem("woo_wishlist"));

  if (!isEmpty(existingWishList)) {
    return existingWishList.productIds.includes(productId);
  } else {
    return false;
  }
};

export const getWishListProducts = () => {
  if (!typeof window !== "undefined") {
    return null;
  }
  return JSON.parse(localStorage.getItem("woo_wishlist"));
};

/**
 * Sanitize markup or text when used inside dangerouslysetInnerHTML
 *
 * @param {string} content Plain or html string.
 *
 * @return {string} Sanitized string
 */
export const sanitize = (content) => {
  return typeof window !== "undefined" ? DOMPurify.sanitize(content) : content;
};

/*export const isCoupon = () => {
  if ( !typeof window !== "undefined" ) {
		return null;
	}
  if (localStorage.getItem("coupon_code")){
  return JSON.parse(localStorage.getItem("coupon_code"))
} else {
  return false
}

}*/
